import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormLabel } from 'react-bootstrap';
import Modal from '../../../commonComponents/modal/Modal';
import translate from '../../../commonComponents/translate';
import FormInput from '../../../commonComponents/forms/FormInput';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import AddressProp from '../../../commonComponents/forms/Address/AddressProp';
import Form from '../../../commonComponents/forms/Form';
import GoogleInput from '../../../commonComponents/forms/inputs/GoogleInput';
import UserProp from '../../../commonComponents/propTypes/userProp';
import VatTypesProp from '../../../commonComponents/forms/Address/VatTypesProp';
import FormValidation from '../../../services/formValidation';

function AddAddressModal(props) {
  const {
    t,
    onClose,
    countries,
    address,
    userProfile,
    isShippingAddress,
    vatTypes,
  } = props;
  const initialState =
    userProfile && isShippingAddress
      ? {
          ...address,
          phone: address?.phone || userProfile?.phone || '',
          firstName: address?.firstName || userProfile?.firstName || '',
          lastName: address?.lastName || userProfile?.lastName || '',
          email: address?.email || userProfile?.email || '',
        }
      : address || {};
  const [state, setState] = useState(initialState || {});

  const composeAddress = () => {
    let addressComposed = '';
    if (state.address_1) {
      addressComposed += state.address_1;
    }
    if (state.address_2) {
      addressComposed += `, ${state?.address_2}`;
    }
    if (state.zipCode) {
      addressComposed += `, ${state?.zipCode}`;
    }
    if (state.city) {
      addressComposed += `, ${state?.city}`;
    }
    if (state.country) {
      addressComposed += `, ${state?.country}`;
    }
    return addressComposed;
  };

  const handleChange = field => value => {
    if (field === 'countryCode') {
      setState(p => ({
        ...p,
        countryCode: value,
        country: countries.find(c => c.code === value).name,
      }));
    } else {
      setState(p => ({
        ...p,
        [field]: value,
      }));
    }
  };
  const handleClose = save => () => {
    onClose(
      save
        ? {
            ...state,
            address: composeAddress(),
          }
        : null,
    );
  };

  const handleAddressChange = newAddress => {
    if (newAddress) {
      setState(prev => ({
        ...prev,
        ...newAddress,
        address_1: newAddress.address_1,
      }));
    }
  };

  const validationRules = {
    phone: { maxlength: 20, required: true },
    address_1: { required: true, maxlength: 35 },
    address_2: { maxlength: 35 },
    zipCode: { required: true },
    city: { required: true },
    countryCode: { required: true },
  };

  const handleOnAddressChange = field => addressParam => {
    if (addressParam) {
      setState(prevState => ({ ...prevState, [field]: addressParam }));
    }
  };

  /** Profile - Contacts - Shipping Address */
  // Make sure that the logic below is only applied if a user is businessType => individual
  // or the user doesn't have businessType
  const { businessType } = userProfile?.personalInfo || {};
  const isCompany = !(!businessType || businessType === 'individual');
  // Check if it's a new shipping address
  const isNewShippingAddress =
    isShippingAddress &&
    userProfile &&
    userProfile.shippingCountry &&
    address === AddressProp.defaultProp;
  // Check if existing address country is equal to Shipping country if so, disabled the input
  const equalToShippingCountry =
    userProfile &&
    userProfile.shippingCountry &&
    userProfile.shippingCountry === address?.countryCode;
  // Country sent to filter
  // const countryToFilterBy = isShippingAddress ? (state?.countryCode || userProfile.shippingCountry) : state?.countryCode;
  const [countryToFilterBy, setCountryToFilterBy] = useState(null);
  useEffect(() => {
    if (state.countryCode?.length > 0) {
      setCountryToFilterBy(state?.countryCode);
    }
    if (isShippingAddress) {
      setCountryToFilterBy(state?.countryCode || userProfile.shippingCountry);
    }
  }, [state.countryCode]);
  useEffect(() => {
    // If it's a new shipping address for user fill the country code automatically
    if (isNewShippingAddress) {
      setState(prevState => ({
        ...prevState,
        countryCode: userProfile.shippingCountry,
      }));
    }
  }, []);

  const createGoogleInput = value => {
    const inputLabel = t('access_session.modal.address1');
    return (
      <div className="row mb-3">
        <FormLabel
          label={inputLabel}
          id="address_1"
          className="col-3 text-right col-form-label"
        >
          {inputLabel}
        </FormLabel>
        <div className="col-7">
          <GoogleInput
            id="address_1"
            name="address_1"
            onChange={handleAddressChange}
            placeholder={inputLabel}
            updateValue={value}
            defaultValue={state.address || ''}
            onSetValue={handleOnAddressChange('address_1')}
            filterByCountry={countryToFilterBy}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (userProfile) {
      const stateVatType = vatTypes.find(
        tax => tax.taxCode === userProfile?.personalInfo?.taxCode,
      );
      // Only check if user is company and if there is billing address
      if (
        userProfile &&
        !isShippingAddress &&
        stateVatType &&
        userProfile?.personalInfo?.businessType !== 'individual' &&
        userProfile?.personalInfo?.businessType !== ''
      ) {
        FormValidation.config();
        FormValidation.init('#addAddressForm', {
          rules: {
            countryCode: { ifDifferent: stateVatType.countryCode },
          },
          messages: {
            countryCode: {
              ifDifferent: t(
                'profile.contacts.error.billingAddress.countryCode',
                stateVatType.countryName,
              ),
            },
          },
        });
      }
    }
  }, [state]);

  return (
    <Modal onClose={handleClose(false)} id="addAddressModal">
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <div
            id="modalBody"
            className="modal-body address-modal"
            style={{ maxHeight: '70vh', overflowY: 'auto' }}
          >
            <Form
              className=""
              id="addAddressForm"
              submitMethod={handleClose(true)}
              validationRules={validationRules}
            >
              {submitHandler => (
                <>
                  <h5>{t('access_session.detail.editAddress')}</h5>
                  {/* PROFILE USER ONLY - Shipping Address */}
                  {userProfile && isShippingAddress && (
                    <>
                      <div className="row">
                        <div className="col-lg-12">
                          <FormInput
                            id="firstName"
                            value={state.firstName}
                            name="firstName"
                            placeholder={t('profile_firstName')}
                            type="text"
                            labelClassName="col-3 text-right"
                            containerClassName="col-7"
                            groupProps={{ className: 'row' }}
                            label={t('profile_firstName')}
                            onChange={handleChange('firstName')}
                            inputProps={{
                              autoComplete: 'new-firstName',
                            }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12">
                          <FormInput
                            id="lastName"
                            value={state.lastName}
                            name="lastName"
                            placeholder={t('profile_lastName')}
                            type="text"
                            labelClassName="col-3 text-right"
                            containerClassName="col-7"
                            groupProps={{ className: 'row' }}
                            label={t('profile_lastName')}
                            onChange={handleChange('lastName')}
                            inputProps={{
                              autoComplete: 'new-lastName',
                            }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12">
                          <FormInput
                            id="email"
                            value={state.email}
                            name="email"
                            placeholder={t('profile_email')}
                            type="email"
                            labelClassName="col-3 text-right"
                            containerClassName="col-7"
                            groupProps={{ className: 'row' }}
                            label={t('profile_email')}
                            onChange={handleChange('email')}
                            inputProps={{
                              autoComplete: 'new-email',
                            }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12">
                          <FormInput
                            id="phone"
                            value={state.phone}
                            name="phone"
                            placeholder={t('profile_phone')}
                            type="text"
                            labelClassName="col-3 text-right"
                            containerClassName="col-7"
                            groupProps={{ className: 'row' }}
                            label={t('profile_phone')}
                            onChange={handleChange('phone')}
                            inputProps={{
                              autoComplete: 'new-phone',
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="row">
                    <div className="col-lg-12">
                      {createGoogleInput(state?.address_1 || '')}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      <FormInput
                        id="address_2"
                        value={state?.address_2 || ''}
                        name="address_2"
                        placeholder={t('access_session.modal.address2')}
                        type="text"
                        labelClassName="col-3 text-right"
                        containerClassName="col-7"
                        groupProps={{ className: 'row' }}
                        label={t('access_session.modal.address2')}
                        onChange={handleChange('address_2')}
                        inputProps={{
                          autoComplete: 'new-address2',
                          maxLength: 35,
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      <FormInput
                        id="zipCode"
                        value={state?.zipCode || ''}
                        name="zipCode"
                        placeholder={t('access_session.modal.zipCode')}
                        type="text"
                        labelClassName="col-3 text-right"
                        containerClassName="col-7"
                        groupProps={{ className: 'row' }}
                        label={t('access_session.modal.zipCode')}
                        onChange={handleChange('zipCode')}
                        inputProps={{
                          autoComplete: 'new-zipCode',
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      <FormInput
                        id="city"
                        value={state?.city || ''}
                        name="city"
                        placeholder={t('access_session.modal.city')}
                        type="text"
                        labelClassName="col-3 text-right"
                        containerClassName="col-7"
                        groupProps={{ className: 'row' }}
                        label={t('access_session.modal.city')}
                        onChange={handleChange('city')}
                        inputProps={{
                          autoComplete: 'new-city',
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      <FormInput
                        id="state"
                        value={state?.state || ''}
                        name="state"
                        placeholder={t('access_session.modal.state')}
                        type="text"
                        labelClassName="col-3 text-right"
                        containerClassName="col-7"
                        groupProps={{ className: 'row' }}
                        label={t('access_session.modal.state')}
                        onChange={handleChange('state')}
                        inputProps={{
                          autoComplete: 'new-state',
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      <FormInput
                        id="countryCode"
                        value={
                          isNewShippingAddress &&
                          userProfile.shippingCountry &&
                          !isCompany
                            ? userProfile.shippingCountry
                            : state.countryCode
                        }
                        name="countryCode"
                        placeholder={t('access_session.modal.country')}
                        type="select"
                        label={t('access_session.modal.country')}
                        labelClassName="col-3 text-right"
                        containerClassName="col-7"
                        groupProps={{ className: 'row' }}
                        disabled={
                          isShippingAddress &&
                          (isNewShippingAddress || equalToShippingCountry) &&
                          !isCompany
                        }
                        onChange={handleChange('countryCode')}
                        inputProps={{
                          searchable: true,
                          items: countries.map(c => ({
                            id: c.code,
                            text: `${c.flagEmoji} ${c.name}`,
                          })),
                          autoComplete: 'new-country',
                          dropdownParent: '#addAddressModal',
                        }}
                        hint={
                          isShippingAddress &&
                          (isNewShippingAddress || equalToShippingCountry) &&
                          !isCompany
                            ? t('modal.address.shippingCountry.hint')
                            : null
                        }
                      />
                    </div>
                  </div>
                  <div className="modal-buttons">
                    <div className="row justify-content-end">
                      <div className="col-lg-3 button-cancel">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleClose(false)}
                        >
                          {t('access_session.modal.cancel')}
                        </button>
                      </div>
                      <div className="col-lg-3">
                        <button
                          type="button"
                          className="btn btn-dynamic"
                          onClick={submitHandler}
                        >
                          {t('access_session.modal.confirm')}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </Form>
          </div>
        </div>
      </div>
    </Modal>
  );
}

AddAddressModal.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  address: PropTypes.shape(AddressProp.propType),
  /** CONTACTS FORM - begin */
  // This component is used in Sessions as well, making sure that the address validation
  // with shipping country is only used on Profile
  userProfile: PropTypes.shape(UserProp.propType),
  isShippingAddress: PropTypes.bool,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  /** CONTACTS FORM - end */
};
AddAddressModal.defaultProps = {
  onClose: () => {},
  address: AddressProp.defaultProp,
  userProfile: null,
  isShippingAddress: false,
};

export default translate(AddAddressModal);
